const EMPTY_CHUNKS = { header: "", lines: [] };

const callTranslator = (translator, key, fallback = "", params, n) => {
  if (typeof translator === "function") {
    return translator(key, fallback, params, n);
  }

  if (!fallback) {
    return "";
  }

  if (!params) {
    return fallback;
  }

  return fallback.replace(/\{(\w+)\}/g, (match, capture) => {
    return Object.prototype.hasOwnProperty.call(params, capture) ? params[capture] : match;
  });
};

const formatPartialMatchAmount = (value, { locale, currencyCode, currencySign } = {}) => {
  if (value === undefined || value === null || value === "") {
    return "";
  }

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return `${value}`;
  }

  const hasDecimals = !Number.isInteger(numericValue);
  const fractionDigits = hasDecimals
    ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    : { minimumFractionDigits: 0, maximumFractionDigits: 0 };

  if (currencySign) {
    return `${currencySign}${numericValue.toLocaleString(locale, fractionDigits)}`;
  }

  if (currencyCode) {
    const upperCurrency = currencyCode.toUpperCase();
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: upperCurrency,
        ...fractionDigits,
      }).format(numericValue);
    } catch (error) {
      return `${numericValue.toLocaleString(locale, fractionDigits)} ${upperCurrency}`;
    }
  }

  return numericValue.toLocaleString(locale, fractionDigits);
};

const buildPartialMatchChunksFromObject = ({
  data,
  translator,
  locale,
  currencyCode,
  currencySign,
}) => {
  if (!data || typeof data !== "object") {
    return EMPTY_CHUNKS;
  }

  const goal = data.goal || {};
  const recurring = data.recurring || {};

  const formatAmount = (value) =>
    formatPartialMatchAmount(value, { locale, currencyCode, currencySign });

  const recurringIntervalKey = recurring.interval;
  const recurringIntervalLabel = recurringIntervalKey
    ? callTranslator(
        translator,
        `dashboard.recurring_interval_${recurringIntervalKey}`,
        recurringIntervalKey
      )
    : "";

  const formatted = {
    donated_actual: formatAmount(data.donated_actual),
    matched_actual: formatAmount(data.matched_actual),
    matched_effective: formatAmount(data.matched_effective),
    unmatched_actual: formatAmount(data.unmatched_actual),
    effective: formatAmount(data.effective),
    previous_total: formatAmount(data.previous_total),
    goal_amount: formatAmount(goal.amount),
    goal_multiplier: goal.multiplier,
    goal_label: typeof goal.label === "string" ? goal.label.trim() : "",
    recurring_count: recurring.count,
    recurring_interval: recurringIntervalLabel,
    recurring_total: formatAmount(recurring.total),
  };

  const lines = [];
  const addLine = (key, fallback, fields) => {
    const params = {};
    let hasValue = true;

    fields.forEach((field) => {
      const value = formatted[field];
      if (value === undefined || value === null || value === "") {
        hasValue = false;
      } else {
        params[field] = value;
      }
    });

    if (hasValue) {
      lines.push(callTranslator(translator, key, fallback, params));
    }
  };

  addLine("dashboard.partial_match_note_donated_actual", "Donation actual: {donated_actual}", [
    "donated_actual",
  ]);
  addLine("dashboard.partial_match_note_matched_actual", "Matched actual: {matched_actual}", [
    "matched_actual",
  ]);
  addLine(
    "dashboard.partial_match_note_matched_effective",
    "Matched effective: {matched_effective}",
    ["matched_effective"]
  );
  addLine("dashboard.partial_match_note_unmatched_actual", "Unmatched actual: {unmatched_actual}", [
    "unmatched_actual",
  ]);
  addLine("dashboard.partial_match_note_effective", "Effective total: {effective}", ["effective"]);
  addLine(
    "dashboard.partial_match_note_recurring",
    "Recurring: {recurring_count} {recurring_interval}(s), total {recurring_total}",
    ["recurring_count", "recurring_interval", "recurring_total"]
  );

  const metaParts = [];

  const goalLabel =
    formatted.goal_label ||
    callTranslator(translator, "dashboard.partial_match_note_goal_label", "Matching goal");

  if (
    formatted.goal_amount &&
    formatted.goal_multiplier !== undefined &&
    formatted.goal_multiplier !== null
  ) {
    metaParts.push(
      callTranslator(
        translator,
        "dashboard.partial_match_note_goal_summary",
        "{label}: {goal_multiplier}x up to {goal_amount}",
        {
          label: goalLabel,
          goal_multiplier: formatted.goal_multiplier,
          goal_amount: formatted.goal_amount,
        }
      )
    );
  } else if (formatted.goal_multiplier !== undefined && formatted.goal_multiplier !== null) {
    metaParts.push(
      callTranslator(
        translator,
        "dashboard.partial_match_note_goal_multiplier",
        "{label}: {goal_multiplier}x match",
        {
          label: goalLabel,
          goal_multiplier: formatted.goal_multiplier,
        }
      )
    );
  } else if (formatted.goal_amount) {
    metaParts.push(
      callTranslator(
        translator,
        "dashboard.partial_match_note_goal_amount",
        "{label}: up to {goal_amount}",
        {
          label: goalLabel,
          goal_amount: formatted.goal_amount,
        }
      )
    );
  }

  if (formatted.previous_total) {
    metaParts.push(
      callTranslator(
        translator,
        "dashboard.partial_match_note_previous_total",
        "Previous total before donation: {previous_total}",
        { previous_total: formatted.previous_total }
      )
    );
  }

  const header =
    metaParts.filter((part) => part && part.length > 0).join(" · ") ||
    callTranslator(translator, "dashboard.partial_match_note_header", "Partial match");

  return {
    header,
    lines,
  };
};

export const buildPartialMatchChunks = ({ note, donation, translator, locale }) => {
  if (!note || typeof note !== "object") {
    return EMPTY_CHUNKS;
  }

  const currencyCode = donation?.attributes?.currency_code;
  const currencySign = donation?.attributes?.currency_sign;

  return buildPartialMatchChunksFromObject({
    data: note,
    translator,
    locale,
    currencyCode,
    currencySign,
  });
};

export const EMPTY_PARTIAL_MATCH_CHUNKS = EMPTY_CHUNKS;
